import { debug, isError } from "@sentry/core";
import { Component, createElement, isValidElement, version } from "react";
const isAtLeastReact17 = (reactVersion) => {
    const reactMajor = reactVersion.match(/^([^.]+)/);
    return reactMajor !== null && parseInt(reactMajor[0]) >= 17;
};
const setCause = (error, cause) => {
    const seenErrors = new WeakSet();
    const recurse = (error, cause) => {
        // Bail out on a cycle in the cause chain to avoid a stack overflow.
        if (seenErrors.has(error)) {
            return;
        }
        if (error.cause) {
            seenErrors.add(error);
            return recurse(error.cause, cause);
        }
        error.cause = cause;
    };
    recurse(error, cause);
};
const INITIAL_STATE = {
    componentStack: null,
    error: null,
    eventId: null,
};
/** An ErrorBoundary component that logs errors to Sentry. */
class ErrorBoundary extends Component {
    state;
    constructor(props) {
        super(props);
        this.state = INITIAL_STATE;
    }
    componentDidCatch(error, errorInfo) {
        const { fallback, captureException } = this.props;
        const { componentStack } = errorInfo;
        const handled = !!fallback;
        // React 17+: link the componentStack to the original error via
        // `error.cause` (requires the `LinkedErrors` integration). `error` may be
        // a non-Error object despite the type, hence the `isError` guard.
        if (isAtLeastReact17(version) && isError(error) && componentStack) {
            const errorBoundaryError = new Error(error.message);
            errorBoundaryError.name = `React ErrorBoundary ${error.name}`;
            errorBoundaryError.stack = componentStack;
            setCause(error, errorBoundaryError);
        }
        // The component stack rides on the hint (per-capture), NOT on the logger's persistent scope,
        // which would stamp every later event with this stack.
        const eventId = captureException(error, {
            mechanism: { handled, type: "auto.function.react.error_boundary" },
            captureContext: { contexts: { react: { componentStack } } },
        }) ?? "";
        // Used over getDerivedStateFromError so componentStack is available.
        this.setState({ error, componentStack, eventId });
    }
    resetErrorBoundary() {
        this.setState(INITIAL_STATE);
    }
    render() {
        const { fallback, children } = this.props;
        const state = this.state;
        // Checked instead of `state.error`, since `null` can itself be thrown.
        if (state.componentStack === null) {
            return typeof children === "function" ? children() : children;
        }
        const element = typeof fallback === "function"
            ? createElement(fallback, {
                error: state.error,
                componentStack: state.componentStack,
                resetError: () => this.resetErrorBoundary(),
                eventId: state.eventId,
            })
            : fallback;
        if (isValidElement(element)) {
            return element;
        }
        if (fallback) {
            debug.warn("fallback did not produce a valid ReactElement");
        }
        return null;
    }
}
export { ErrorBoundary };
